import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSession } from '../IslandRoot'
import BusinessSwitcher from '../businesses/BusinessSwitcher'
import { useBrand } from '../../lib/brand'
import { BOOKME_ONBOARDING_URL, currentProduct, isAdmin, type Product } from '../../lib/product'
import { Icon } from './icons'
import { Menu, type MenuSection } from './Menu'
import { SHELL_CSS } from './shellStyles'
import { useShellData } from './useShellData'
import {
  SCREENS,
  SETTINGS_TAB,
  defaultTab,
  hashForTab,
  isTabVisible,
  screenForTab,
  tabFromHash,
  visibleScreenIds,
  visibleViews,
} from './screens'
import * as legacy from './legacy'

/**
 * App shell (frontend-simplification Phase 3).
 *
 * Five screens — Today, People, Deals, Inbox, Calendar — replace the ten
 * legacy top tabs. Desktop: a collapsible left rail. Phones (< 640px): a
 * fixed bottom bar. The island also renders, through portals into the
 * legacy header:
 *   - #shell-bar-mount: the screen title and its view switch
 *     (Today / Tasks / Activity, People / Companies, Inbox / Outreach)
 *   - #shell-actions-mount: search (⌘K), + New, and the profile menu on phones
 * Settings leaves the nav and opens from the profile menu.
 *
 * Product: the same page is served at /crm/ (Hunts) and /meet/ (BookMe).
 * BookMe opens on Calendar, is titled "BookMe", and shows only Today and
 * Calendar until the user switches the Hunts screens on (profile menu,
 * `show_hunts` setting) or already has contacts.
 *
 * MIGRATION_DEBT: the shell drives the legacy panels through
 * `window.switchTab` (see ./legacy.ts) and mirrors `.nav-tab.active` with a
 * MutationObserver. When the legacy nav is decommissioned the shell becomes
 * the router and both go away.
 */

const COLLAPSED_KEY = 'shaun.rail-collapsed'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true'
  } catch {
    return false
  }
}

function writeCollapsed(value: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, String(value))
  } catch {
    // ignore quota / private-mode errors
  }
}

function writeHash(tab: string): void {
  const hash = hashForTab(tab)
  if (!hash) return
  if (location.hash !== `#${hash}`) {
    // replaceState: a view switch is not a back-button stop.
    history.replaceState(null, '', `#${hash}`)
  }
}

function helpUrl(product: Product): string {
  return product === 'bookme' ? BOOKME_ONBOARDING_URL : '/hunts/onboarding/'
}

function ProfileMenu({
  email,
  product,
  showHunts,
  onToggleHunts,
  placement,
  withEmail,
}: {
  email: string
  product: Product
  showHunts: boolean
  onToggleHunts: () => void
  placement: 'below-end' | 'above-start'
  withEmail: boolean
}) {
  const [theme, setTheme] = useState(legacy.currentTheme)
  const initial = (email.trim().charAt(0) || '?').toUpperCase()
  const sections: MenuSection[] = [
    {
      key: 'account',
      heading: email || undefined,
      items: [
        { key: 'settings', label: 'Settings', onSelect: () => legacy.openSettings() },
        {
          key: 'theme',
          label: 'Dark mode',
          checked: theme === 'dark',
          onSelect: () => {
            legacy.toggleTheme()
            setTheme(legacy.currentTheme())
          },
        },
        ...(product === 'bookme'
          ? [
              {
                key: 'hunts',
                label: 'Show Hunts screens',
                checked: showHunts,
                onSelect: onToggleHunts,
              },
            ]
          : []),
        ...(showHunts
          ? [{ key: 'businesses', label: 'Businesses', onSelect: () => legacy.openBusinesses() }]
          : []),
      ],
    },
    {
      key: 'session',
      items: [
        { key: 'help', label: 'Help and setup', onSelect: () => location.assign(helpUrl(product)) },
        { key: 'signout', label: 'Sign out', onSelect: () => legacy.signOut() },
      ],
    },
  ]
  return (
    <Menu
      label="Profile and settings"
      triggerClassName="shell-profile-btn"
      triggerTitle={email}
      placement={placement}
      sections={sections}
      trigger={
        <>
          <span className="shell-avatar" aria-hidden="true">
            {initial}
          </span>
          {withEmail && <span className="shell-profile-email">{email}</span>}
        </>
      }
    />
  )
}

export default function AppRail() {
  const session = useSession()
  const authed = session.status === 'authed'
  const userId = session.user?.id ?? null
  const email = session.user?.email ?? ''
  const product = useMemo(currentProduct, [])
  const brand = useBrand()
  const { showHunts, hasOutreach, ready, setShowHunts } = useShellData(
    authed ? userId : null,
    product,
  )
  const [activeTab, setActiveTab] = useState<string>(() =>
    typeof document !== 'undefined' ? legacy.readActiveTab() : 'today',
  )
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed)
  const [mounts, setMounts] = useState<{ bar: HTMLElement | null; actions: HTMLElement | null }>({
    bar: null,
    actions: null,
  })

  // Product name in the tab title.
  useEffect(() => {
    if (product === 'bookme') document.title = 'BookMe'
  }, [product])

  // Mirror the legacy `.nav-tab.active` (changed by the rail, ⌘K, links
  // inside panels, openSettings) and keep the URL hash on the canonical name.
  useEffect(() => {
    const nav = document.querySelector('.nav-tabs')
    if (!nav) return
    const sync = () => {
      const next = legacy.readActiveTab()
      setActiveTab(next)
      writeHash(next)
    }
    const observer = new MutationObserver(sync)
    observer.observe(nav, { subtree: true, attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  // Open on the URL's screen, or the product's default (Hunts → Today,
  // BookMe → Calendar). Waits up to ~1s for the legacy nav to exist.
  useEffect(() => {
    if (!authed) return
    let cancelled = false
    let frame = 0
    const started = Date.now()
    const restore = () => {
      if (cancelled) return
      if (!document.querySelector('.nav-tabs')) {
        if (Date.now() - started < 1000) frame = requestAnimationFrame(restore)
        return
      }
      const target = tabFromHash(location.hash) ?? defaultTab(product)
      if (target !== legacy.readActiveTab()) legacy.goToTab(target)
      else writeHash(target)
    }
    restore()
    return () => {
      cancelled = true
      if (frame) cancelAnimationFrame(frame)
    }
  }, [authed, product])

  // Back/forward, bookmarks and typed hashes.
  useEffect(() => {
    if (!authed) return
    const onHash = () => {
      const tab = tabFromHash(location.hash)
      if (tab && tab !== legacy.readActiveTab()) legacy.goToTab(tab)
    }
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [authed])

  // Body classes the host CSS keys off. Cleared on sign-out.
  useEffect(() => {
    const cls = document.body.classList
    if (!authed) return
    cls.add('with-rail')
    cls.toggle('product-bookme', product === 'bookme')
    cls.toggle('show-hunts', showHunts)
    cls.toggle('is-admin', isAdmin(email, location.search))
    return () => {
      cls.remove('with-rail', 'product-bookme', 'show-hunts', 'is-admin')
    }
  }, [authed, product, showHunts, email])

  useEffect(() => {
    writeCollapsed(collapsed)
    if (!authed) {
      document.body.classList.remove('rail-collapsed')
      return
    }
    document.body.classList.toggle('rail-collapsed', collapsed)
    return () => document.body.classList.remove('rail-collapsed')
  }, [collapsed, authed])

  // ⌘B / Ctrl+B toggles the rail.
  useEffect(() => {
    if (!authed) return
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault()
        setCollapsed((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [authed])

  // Header portal targets live in the legacy header.
  useEffect(() => {
    if (!authed) return
    setMounts({
      bar: document.getElementById('shell-bar-mount'),
      actions: document.getElementById('shell-actions-mount'),
    })
  }, [authed])

  // BookMe-only mode: a hidden Hunts view (deep link such as #contacts or
  // #tasks, a link inside a panel, or switching the Hunts screens off) goes
  // to Calendar. Waits for the show-Hunts lookup so users who already have
  // contacts keep their deep link.
  const crmOnly = brand?.crmOnly === true
  const visibleIds = visibleScreenIds(product, showHunts, crmOnly)
  const activeScreen = screenForTab(activeTab)
  useEffect(() => {
    if (!authed || !ready) return
    if (!isTabVisible(activeTab, product, showHunts, crmOnly))
      legacy.goToTab(crmOnly ? 'today' : defaultTab(product))
  }, [authed, ready, product, showHunts, crmOnly, activeTab])

  if (!authed) return null

  const screens = SCREENS.filter((s) => visibleIds.includes(s.id))
  const views = activeScreen
    ? visibleViews(activeScreen, { hasOutreach, activeTab, product, showHunts, crmOnly })
    : []
  const title = activeTab === SETTINGS_TAB ? 'Settings' : (activeScreen?.label ?? '')
  const toggleHunts = () => setShowHunts(!showHunts)

  const newMenu: MenuSection[] = [
    {
      key: 'new',
      items: [
        { key: 'contact', label: 'Contact', onSelect: legacy.newContact },
        { key: 'deal', label: 'Deal', onSelect: legacy.newDeal },
        { key: 'task', label: 'Task', onSelect: legacy.newTask },
      ],
    },
    {
      key: 'import',
      items: [{ key: 'import', label: 'Import contacts (CSV)', onSelect: legacy.importContacts }],
    },
  ]

  const bar = (
    <div className={`shell-bar${views.length > 1 ? ' has-views' : ''}`}>
      <h1 className="shell-title">{title}</h1>
      {views.length > 1 && (
        <div className="shell-views" role="tablist" aria-label={`${title} views`}>
          {views.map((v) => (
            <button
              key={v.tab}
              type="button"
              role="tab"
              className="shell-view"
              aria-selected={activeTab === v.tab}
              onClick={() => legacy.goToTab(v.tab)}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )

  const actions = (
    <div className="shell-actions">
      <button
        type="button"
        className="shell-search"
        onClick={legacy.openSearch}
        aria-label="Search"
      >
        <Icon name="search" size={16} />
        <span>Search</span>
        <kbd>⌘K</kbd>
      </button>
      {showHunts && (
        <Menu
          label="New"
          triggerClassName="shell-new-btn"
          sections={newMenu}
          trigger={
            <>
              <Icon name="plus" size={16} />
              <span>New</span>
            </>
          }
        />
      )}
      <div className="shell-avatar-top">
        <ProfileMenu
          email={email}
          product={product}
          showHunts={showHunts}
          onToggleHunts={toggleHunts}
          placement="below-end"
          withEmail={false}
        />
      </div>
    </div>
  )

  return (
    <>
      <style>{SHELL_CSS}</style>
      <nav className="app-rail-nav" aria-label="Primary">
        <div className="shell-brand">
          {brand?.logo ? (
            <img className="shell-brand-logo" src={brand.logo} alt="" aria-hidden="true" />
          ) : (
            <span className="shell-brand-dot" aria-hidden="true" />
          )}
          <span className="shell-brand-name">
            {brand?.name ?? (product === 'bookme' ? 'BookMe' : 'Hunts')}
          </span>
        </div>
        {showHunts && <BusinessSwitcher collapsed={collapsed} />}
        <div className="app-rail-items">
          {screens.map((s) => {
            const active = activeScreen?.id === s.id
            return (
              <button
                key={s.id}
                type="button"
                className={`app-rail-item${active ? ' active' : ''}`}
                onClick={() => legacy.goToTab(s.views[0]?.tab ?? 'today')}
                aria-current={active ? 'page' : undefined}
                title={collapsed ? s.label : undefined}
              >
                <span className="app-rail-item-icon">
                  <Icon name={s.icon} />
                </span>
                <span className="app-rail-item-label">{s.label}</span>
              </button>
            )
          })}
        </div>
        <div className="shell-rail-foot">
          <ProfileMenu
            email={email}
            product={product}
            showHunts={showHunts}
            onToggleHunts={toggleHunts}
            placement="above-start"
            withEmail={!collapsed}
          />
          <button
            type="button"
            className="app-rail-toggle"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={collapsed}
            title={collapsed ? 'Expand sidebar (⌘B)' : 'Collapse sidebar (⌘B)'}
          >
            <Icon name={collapsed ? 'chevron-right' : 'chevron-left'} />
          </button>
        </div>
      </nav>
      {mounts.bar && createPortal(bar, mounts.bar)}
      {mounts.actions && createPortal(actions, mounts.actions)}
    </>
  )
}
